// src/components/gestionEvento/PruebaRow.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
  CircularProgress,
  Checkbox,
  Box, // Importar Box si es necesario para estilos
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import CategoriaChips from './CategoriaChips';
import PropTypes from 'prop-types';
import CustomPruebaModal from './CustomPruebaModal';
import { alpha } from '@mui/material/styles';

// Define el componente funcional principal
const PruebaRowComponent = ({
  prueba, // Prop original con los datos de la prueba
  categorias,
  onUpdatePrueba, // Función para guardar los cambios (viene del padre)
  onDeletePrueba, // Función para eliminar (viene del padre)
  setSnackbar,    // Función para mostrar notificaciones (viene del padre)
  isSelected,
  handleClick,    // Función para manejar la selección de la fila (viene del padre)
  // predefinedPruebas, // Comentado o eliminado si no se usa directamente aquí 
  onRowDirtyChange,    // 👈 NUEVO
  bulkMode = false,    // 👈 NUEVO
  selectionLocked = false, // 👈 NUEVO
  nameColPx,
  descColPx,
  catColPx,
}) => {

  // --- Estados Locales ---
  // Estado para guardar los datos mientras se editan en esta fila
  const [localEditedData, setLocalEditedData] = useState({ ...prueba });
  // Estado para controlar el diálogo de confirmación de eliminación
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  // Estado para controlar el modal de edición detallada
  const [openEditModal, setOpenEditModal] = useState(false);
  const [originalData, setOriginalData] = useState({ ...prueba }); 
  const NAME_COL_PX = 320;
  const DESC_COL_PX = 360;
  const CAT_COL_PX  = 360;

  // --- Efecto para Sincronizar ---
  // Si la prop 'prueba' cambia desde el exterior (ej: recarga de datos),
  // reseteamos el estado local para reflejar los nuevos datos.
  useEffect(() => {
    setLocalEditedData({ ...prueba });
    setOriginalData({ ...prueba });
  }, [prueba]);

  // --- NUEVO: validador simple y notificación al padre ---
   const buildValidation = (data) => {
     const errors = [];
     const nombre = String(data?.nombre || '').trim();
     if (!nombre) errors.push('Nombre es requerido.');
     const isValid = errors.length === 0;
     const payload = {
       ...data,
       nombre,
       // Aseguramos arrays si vienen undefined
       categoriaIdsFemenil: Array.isArray(data?.categoriaIdsFemenil) ? data.categoriaIdsFemenil : [],
       categoriaIdsVaronil: Array.isArray(data?.categoriaIdsVaronil) ? data.categoriaIdsVaronil : [],
     };
     return { isValid, errors, payload };
   };

     // Calcula si hay cambios pendientes comparando el estado local con la prop original
  const hasChanges = useMemo(() => {
    // Comparamos las versiones stringify para una comparación profunda simple
    return JSON.stringify(localEditedData) !== JSON.stringify(prueba);
  }, [localEditedData, prueba]);


   useEffect(() => {
     if (!onRowDirtyChange) return;
     const { isValid, errors, payload } = buildValidation(localEditedData);
     onRowDirtyChange(prueba.id, {
       hasChanges,
       isValid,
       errors,
       payload,
       original: originalData,
     });
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [hasChanges, localEditedData]);

  // --- Handlers ---

  // Abre el modal de edición detallada
  const handleOpenEditModal = (e) => {
    e.stopPropagation(); // Evita que el click active la selección de la fila
    setOpenEditModal(true);
  };

  // Función centralizada para manejar cambios en los campos locales
  const handleLocalEditChange = (field, value) => {
    setLocalEditedData(prevData => ({
      ...prevData,
      [field]: value,
    }));
  };

  // Handlers específicos para las categorías usando la función centralizada
  const handleCategoriaFemenilChange = (selected) => {
    handleLocalEditChange('categoriaIdsFemenil', selected);
  };

  const handleCategoriaVaronilChange = (selected) => {
    handleLocalEditChange('categoriaIdsVaronil', selected);
  };

  // Maneja el click en el botón "Guardar"
  const handleSaveClick = (event) => {
    event.stopPropagation();
   if (bulkMode) {
     setSnackbar?.({ open: true, message: 'Hay varias filas en edición. Usa "Guardar todas".', severity: 'info' });
     return;
   }
   if (!localEditedData.nombre?.trim()) {
      setSnackbar({ open: true, message: 'El campo "Nombre" es obligatorio.', severity: 'warning' });
      return;
    }
    onUpdatePrueba(prueba.id, localEditedData);
   // Si el update fue exitoso, el padre recarga; localmente, dejamos limpia la fila
   setOriginalData({ ...localEditedData });
  };


  // Maneja el click en el botón "Cancelar"
  const handleCancel = (event) => {
    event.stopPropagation(); // Evita que el click active la selección de la fila
    // Restaura los datos locales a los originales de la prop 'prueba'
    setLocalEditedData({ ...prueba });
    onRowDirtyChange?.(prueba.id, {
     hasChanges: false,
     isValid: true,
     errors: [],
     payload: null,
     original: originalData,
   });
  };

  // Abre el diálogo de confirmación para eliminar
  const handleDeleteClick = (event) => {
    event.stopPropagation();
    setOpenDeleteDialog(true);
  };

  // Confirma la eliminación
  const handleConfirmDelete = () => {
    onDeletePrueba(prueba.id); // Llama a la función del padre para eliminar
    setOpenDeleteDialog(false); // Cierra el diálogo
    // El snackbar de éxito/info debería manejarse en el padre
  };

  // --- Renderizado ---

  // Estilos (puedes definirlos fuera o mantenerlos aquí si son simples)
  const editableFieldSx = (t) => ({
   '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
   '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
   '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
   '& .MuiInputBase-root': {
      backgroundColor: alpha(t.palette.primary.main, 0.06),
      borderRadius: 4,
      transition: 'background-color .2s',
    },
    '& .MuiInputBase-root.Mui-focused, &:hover .MuiInputBase-root': {
      backgroundColor: alpha(t.palette.primary.main, 0.10),
    },
  });

  const fieldStyles = {
    nombre: { width: '100%' },
    // solo mostrar 1 línea de chips; lo demás se corta
    categoria: (t) => ({
      width: '100%',
      maxHeight: 48,          // solo se ve la primera línea de chips
      overflow: 'hidden',
      // sin borde y con fondo suave como en categorías
      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
      '& .MuiOutlinedInput-root': {
        backgroundColor: alpha(t.palette.primary.main, 0.06),
        borderRadius: 4,
      },
      '& .MuiOutlinedInput-root.Mui-focused, & .MuiOutlinedInput-root:hover': {
        backgroundColor: alpha(t.palette.primary.main, 0.10),
      },
    }),
    descripcion: { width: '100%' },
  };

  // Muestra un estado de carga si la prueba se está cargando (si tienes esa lógica)
  if (prueba.estado === 'cargando') {
    return (
      <TableRow>
        <TableCell colSpan={5} align="center">
          <CircularProgress size={20} />
          Cargando prueba...
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {/* La fila de la tabla */}
      <TableRow hover selected={isSelected}>
        {/* Checkbox de Selección */}
      <TableCell
        padding="checkbox"
        onClick={(e) => {
          if (selectionLocked) { e.preventDefault(); e.stopPropagation(); return; }
          handleClick(e, prueba.id);
        }}
      >
        <Tooltip title={selectionLocked ? 'No puedes seleccionar mientras hay edición en curso' : ''}>
          <Checkbox
            color="primary"
            checked={isSelected}
            disabled={selectionLocked}
            inputProps={{ 'aria-labelledby': `prueba-${prueba.id}` }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              if (selectionLocked) return;
              handleClick(e, prueba.id);
            }}
          />
        </Tooltip>
      </TableCell>

        {/* Nombre de la Prueba (Editable) */}
<TableCell component="th" scope="row" padding="none" sx={{ pr: 1, width: nameColPx, maxWidth: nameColPx }}>
          <Box sx={fieldStyles.nombre}>
            <TextField
              value={localEditedData.nombre || ''} // Controlado por el estado local
              onChange={(e) => handleLocalEditChange('nombre', e.target.value)} // Actualiza estado local
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Nombre de la prueba"
              sx={editableFieldSx}
              inputProps={{ style: { textOverflow: 'ellipsis' } }}
            />
          </Box>
        </TableCell>

        {/* Categorías Femenil (Editable) */}
        <TableCell sx={{ minWidth: catColPx }}>
          <Box sx={fieldStyles.categoria}>
            <CategoriaChips
              categorias={categorias}
              selectedCategorias={localEditedData.categoriaIdsFemenil || []} // Controlado por estado local
              setSelectedCategorias={handleCategoriaFemenilChange} // Actualiza estado local
              allLabel="Todas Femenil" // Etiqueta más corta
              size="small" // Chips más pequeños
            />
          </Box>
        </TableCell>

        {/* Categorías Varonil (Editable) */}
        <TableCell sx={{ minWidth: catColPx }}>
          <Box sx={fieldStyles.categoria}>
            <CategoriaChips
              categorias={categorias}
              selectedCategorias={localEditedData.categoriaIdsVaronil || []} // Controlado por estado local
              setSelectedCategorias={handleCategoriaVaronilChange} // Actualiza estado local
              allLabel="Todas Varonil" // Etiqueta más corta
              size="small" // Chips más pequeños
            />
          </Box>
        </TableCell>

    {/* Descripción (nuevo) */}
       <TableCell sx={{ width: descColPx, maxWidth: descColPx, pr: 1 }}>
         <Box sx={fieldStyles.descripcion}>
           <TextField
             value={localEditedData.descripcion || ''}
             onChange={(e) => handleLocalEditChange('descripcion', e.target.value)}
             variant="outlined"
             size="small"
             fullWidth
             placeholder="Descripción (opcional)"
             inputProps={{ maxLength: 120 }}
             sx={editableFieldSx}
           />
         </Box>
       </TableCell>

        {/* Acciones (Guardar/Cancelar o Editar/Eliminar) */}
        <TableCell>
         {hasChanges ? (
           // 👇 En modo bulk (2+ filas editándose) ocultamos Guardar y dejamos solo Cancelar
           bulkMode ? (
             <Tooltip title="Cancelar cambios de esta fila">
               <IconButton color="secondary" onClick={handleCancel}>
                 <CancelIcon />
               </IconButton>
             </Tooltip>
           ) : (
             <>
               <Tooltip title="Guardar Cambios">
                 <span>
                   <IconButton
                     color="primary"
                     onClick={handleSaveClick}
                     disabled={!localEditedData.nombre?.trim()}
                   >
                     <SaveIcon />
                   </IconButton>
                 </span>
               </Tooltip>
               <Tooltip title="Cancelar Cambios">
                 <IconButton color="secondary" onClick={handleCancel}>
                   <CancelIcon /> 
                 </IconButton>
               </Tooltip>
             </>
           )
         ) : (
           <>
             <Tooltip title="Editar Prueba (Detalles)">
               <IconButton color="primary" onClick={handleOpenEditModal}>
                 <EditIcon />
               </IconButton>
             </Tooltip>
             {/* En bulk seguimos ocultando Eliminar */}
             {!bulkMode && (
               <Tooltip title="Eliminar Prueba">
                 <IconButton color="error" onClick={handleDeleteClick}>
                   <DeleteIcon />
                 </IconButton>
               </Tooltip>
             )}
           </>
         )}
        </TableCell>
      </TableRow>

      {/* Modal de Edición Detallada */}
      <CustomPruebaModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        // Al guardar desde el modal, llama a onUpdatePrueba con los datos locales
        onSave={() => {
           // Validaciones opcionales aquí también si el modal puede dejar datos inválidos
           if (!localEditedData.nombre?.trim() || !localEditedData.testType) {
             setSnackbar({ open: true, message: 'Nombre y Tipo de Prueba son obligatorios.', severity: 'warning' });
             return; // No guardar ni cerrar si es inválido
           }
           onUpdatePrueba(prueba.id, localEditedData);
           setOpenEditModal(false);
        }}
        pruebaData={localEditedData} // Pasa el estado local al modal
        onDataChange={setLocalEditedData} // Permite que el modal actualice el estado local
        categorias={categorias}
      />

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Eliminar Prueba</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro que deseas eliminar la prueba "{prueba.nombre}"? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete} // Llama al handler que ejecuta onDeletePrueba
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// --- PropTypes ---
PruebaRowComponent.propTypes = {
  prueba: PropTypes.shape({
    id: PropTypes.string.isRequired,
    nombre: PropTypes.string, // Puede ser null/undefined inicialmente? Ajustar si es necesario
    categoriaIdsFemenil: PropTypes.arrayOf(PropTypes.string),
    categoriaIdsVaronil: PropTypes.arrayOf(PropTypes.string),
    testType: PropTypes.string,
    descripcion: PropTypes.string,
    eventCode: PropTypes.string,
    tipoMarca: PropTypes.string,
    estado: PropTypes.string, // Si usas el estado de carga
  }).isRequired,
  categorias: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ).isRequired,
  onUpdatePrueba: PropTypes.func.isRequired,
  onDeletePrueba: PropTypes.func.isRequired,
  setSnackbar: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  onRowDirtyChange: PropTypes.func,
  bulkMode: PropTypes.bool,
  selectionLocked: PropTypes.bool, 
  // predefinedPruebas: PropTypes.array, // Si lo necesitas pasar por alguna razón
};

// Exporta la versión memoizada del componente
// React.memo ayuda a evitar re-renders si las props no han cambiado (comparación superficial)
// Es más efectivo cuando las props complejas (objetos, arrays, funciones) son estables (misma referencia)
const PruebaRow = React.memo(PruebaRowComponent);

export default PruebaRow;